import React, { createContext, useContext, useEffect, useState } from 'react';
import ProfileUpdateModal from '~/components/ProfileUpdateModal';
import SocialReminderModal from '~/components/ProfileUpdateModal/SocialReminderModal/SocialReminderModal';
import SocialNetworkUpdateModal from '~/components/ProfileUpdateModal/SocialNetworkUpdateModal/SocialNetworkUpdateModal';
import api from '~/services/api';
import Snackbar from '~/util/SnackBar';
import { LOJA_MULTIMARCA, CAIXA } from '~/util/perfilId';
import { CRISBARROS } from '~/util/brands';

const SocialsContext = createContext();

const MAX_DISMISS_PROFILE = 5;
const MAX_DISMISS_REMINDER = 3;
const REMINDER_DAYS = 90;

export const SocialsProvider = ({ userProfile, children }) => {
    const [openProfileModal, setOpenProfileModal] = useState(false);
    const [openSocialUpdateModal, setOpenSocialUpdateModal] = useState(false);
    const [hasSocialProfile, setHasSocialProfile] = useState(null);
    const [showReminder, setShowReminder] = useState(false);
    const [socialData, setSocialData] = useState(null);

    const isFromBlockedBrands = userProfile?.users_brands?.some(
        brand => brand.id_marca === CRISBARROS
    );

    const getStorageKey = key => `${key}:${userProfile.id_usuario_mais_vendas}`;

    const storage = {
        get: key => localStorage.getItem(getStorageKey(key)),
        set: (key, value) => localStorage.setItem(getStorageKey(key), value),
        remove: key => localStorage.removeItem(getStorageKey(key)),
    };

    const isEmpty = value => !value || value.trim() === '';

    const getDaysSince = dateStr => {
        const diffMs = new Date().getTime() - new Date(dateStr).getTime();
        return diffMs / (1000 * 60 * 60 * 24);
    };

    const checkSocialProfile = async () => {
        try {
            const response = await api.get(
                `salesperson/${userProfile.id_usuario_mais_vendas}/social-network/`
            );
            const data = response.data;
            setSocialData(data);
            const hasAny =
                !isEmpty(data.instagram_user) ||
                !isEmpty(data.tiktok_user) ||
                !isEmpty(data.phone);
            setHasSocialProfile(hasAny);
            return hasAny;
        } catch {
            setHasSocialProfile(false);
            return false;
        }
    };

    const shouldShowProfileModal = () => {
        const count = parseInt(
            storage.get('profileModal:dismissCount') || '0',
            10
        );
        if (count >= MAX_DISMISS_PROFILE) return false;

        const last = storage.get('profileModal:lastDismissed');
        if (!last) return true;

        const now = new Date();
        const lastDate = new Date(last);
        return now.toDateString() !== lastDate.toDateString();
    };

    const shouldShowReminderModal = () => {
        const count = parseInt(
            storage.get('socialReminderModal:dismissCount') || '0',
            10
        );
        if (count >= MAX_DISMISS_REMINDER) return false;

        const lastConfirmed = socialData?.updated_at;
        const fallbackConfirmed = storage.get(
            'socialReminderModal:lastConfirmed'
        );

        const referenceDate = lastConfirmed || fallbackConfirmed;
        if (!referenceDate) return true;

        return getDaysSince(referenceDate) >= REMINDER_DAYS;
    };

    const handleProfileClose = () => {
        const currentCount = parseInt(
            storage.get('profileModal:dismissCount') || '0',
            10
        );
        storage.set('profileModal:dismissCount', (currentCount + 1).toString());
        storage.set('profileModal:lastDismissed', new Date().toISOString());
        setOpenProfileModal(false);
    };

    const handleProfileSubmit = async data => {
        try {
            await api.post(
                `salesperson/${userProfile.id_usuario_mais_vendas}/social-network/`,
                data
            );
            Snackbar.topSuccess('Redes sociais salvas com sucesso!');
            setOpenProfileModal(false);
        } catch {
            Snackbar.topError('Erro ao salvar redes sociais');
        }
    };

    const handleReminderConfirm = () => {
        setOpenSocialUpdateModal(true);
        setShowReminder(false);
    };

    const handleReminderClose = () => {
        const currentCount = parseInt(
            storage.get('socialReminderModal:dismissCount') || '0',
            10
        );
        storage.set(
            'socialReminderModal:dismissCount',
            (currentCount + 1).toString()
        );
        setShowReminder(false);
    };

    const handleSocialUpdateSubmit = async data => {
        try {
            await api.patch(
                `salesperson/${userProfile.id_usuario_mais_vendas}/social-network/`,
                data
            );

            storage.remove('profileModal:dismissCount');
            storage.remove('profileModal:lastDismissed');
            storage.remove('socialReminderModal:dismissCount');
            storage.set(
                'socialReminderModal:lastConfirmed',
                new Date().toISOString()
            );

            Snackbar.topSuccess('Redes sociais salvas com sucesso!');
            setOpenSocialUpdateModal(false);
            refetchSocialProfile();
        } catch {
            Snackbar.topError('Erro ao salvar redes sociais');
        }
    };

    const refetchSocialProfile = async () => {
        await checkSocialProfile();
    };

    useEffect(() => {
        let isMounted = true;

        const runChecks = async () => {
            const hasProfile = await checkSocialProfile();
            if (!isMounted) return;

            if (!hasProfile && shouldShowProfileModal()) {
                setOpenProfileModal(false);
            }

            if (
                hasProfile &&
                socialData?.updated_at &&
                shouldShowReminderModal()
            ) {
                setShowReminder(true);
            }
        };

        if (
            userProfile?.id_usuario_mais_vendas &&
            userProfile?.id_perfil !== CAIXA &&
            userProfile?.id_perfil !== LOJA_MULTIMARCA &&
            !isFromBlockedBrands
        ) {
            runChecks();
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line
    }, [userProfile]);

    return (
        <SocialsContext.Provider
            value={{
                hasSocialProfile,
                socialData,
                setOpenSocialUpdateModal,
                refetchSocialProfile,
            }}
        >
            {children}

            {hasSocialProfile && showReminder && (
                <SocialReminderModal
                    open={showReminder}
                    onConfirm={handleReminderConfirm}
                    onClose={handleReminderClose}
                />
            )}

            <ProfileUpdateModal
                open={openProfileModal}
                onClose={handleProfileClose}
                onSubmit={handleProfileSubmit}
            />

            <SocialNetworkUpdateModal
                open={openSocialUpdateModal}
                onClose={() => setOpenSocialUpdateModal(false)}
                onSubmit={handleSocialUpdateSubmit}
                socialData={socialData}
            />
        </SocialsContext.Provider>
    );
};

export const useSocials = () => useContext(SocialsContext);
